import { useEffect, useSyncExternalStore } from "react";
import { api } from "./api.js";

/**
 * The office's user groups, for the mention picker and for rendering `@handle`
 * as a chip.
 *
 * Read once per page load and shared: every message on screen asks whether its
 * `@word` is a group, and a fetch per message — or per mounted composer — would
 * be the same small list many times over. Admin edits call `reloadUserGroups`.
 */
export type UserGroup = {
  id: string;
  name: string;
  handle: string;
  description: string | null;
  memberIds: string[];
};

let groups: UserGroup[] = [];
let state: "idle" | "loading" | "loaded" = "idle";
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

async function fetchAll(): Promise<UserGroup[]> {
  const all: UserGroup[] = [];
  let after: string | null = null;
  // Paginated server-side; an office has tens of groups, so this is one page
  // in practice, but it never silently stops at the first.
  for (let page = 0; page < 20; page += 1) {
    const query: string = after ? `?after=${encodeURIComponent(after)}` : "";
    const res: { groups: UserGroup[]; nextCursor: string | null } = await api.get(`/user-groups${query}`);
    all.push(...res.groups);
    if (!res.nextCursor) break;
    after = res.nextCursor;
  }
  return all;
}

export function reloadUserGroups(): void {
  state = "loading";
  fetchAll()
    .then((next) => {
      groups = next;
    })
    // No groups is a safe answer: `@handle` then reads as plain text.
    .catch(() => undefined)
    .finally(() => {
      state = "loaded";
      emit();
    });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useUserGroups(): UserGroup[] {
  const value = useSyncExternalStore(subscribe, () => groups, () => groups);
  useEffect(() => {
    if (state === "idle") reloadUserGroups();
  }, []);
  return value;
}

/** For tests: replace the cached list without a request. */
export function setUserGroupsForTest(next: UserGroup[]): void {
  groups = next;
  state = "loaded";
  emit();
}
